import { BaseDialog } from '../../../../common/dialogs/BaseDialog';
import { DialogContent, DialogTitle, DialogActions, Box } from '@mui/material';
import { Dispatch, useState } from 'react';
import { FunnelToDeploy } from './FunnelToDeploy';
import { EnvironmentToDeploy } from './EnvironmentToDeploy';
import { DeploymentDiff } from '../deployment-changes/DeploymentDiff';
import { Typography, Button, IconButton, colors } from '@sweep-io/sweep-design';
import { ArrowLeft, Close } from '@sweep-io/sweep-design/dist/icons';
import { CrmOrg } from '../../../../../types/CrmOrgs';
import { usePermissions } from '../../../../common/permissions/usePermissions';

interface DeployContentProps {
  selectedFunnelId: string;
  setSelectedFunnelId: (id: string) => void;
  selectedOrgId?: string;
  setSelectedOrgId: Dispatch<any>;
  onConfirmDeploy: () => void;
  onRequestChanges: () => void;
  onCancel: () => void;
  orgs: CrmOrg[];
  onNoteChange?: (note: string) => void;
  funnels: Funnel[];
  initialCrmOrgId: string;
  initialStep?: 'step1' | 'step2' | 'step3';
}

const DeployContent = ({
  selectedFunnelId,
  setSelectedFunnelId,
  selectedOrgId,
  setSelectedOrgId,
  onConfirmDeploy,
  onRequestChanges,
  onCancel,
  onNoteChange,
  orgs,
  funnels,
  initialCrmOrgId,
  initialStep,
}: DeployContentProps) => {
  const hasOnlyOneFunnel = funnels.length === 1;

  const step1 = {
    id: 'step1',
    title: 'Choose funnel to deploy',
    showBackButton: () => false,
    getSecondaryText: () => '',
    backActionCallback: () => undefined,
  };

  const step2 = {
    id: 'step2',
    title: 'Deploy funnel to environment',
    showBackButton: () => !hasOnlyOneFunnel,
    getSecondaryText: (): string => 'Preview deployed changes',
    backActionCallback: () => {
      if (!hasOnlyOneFunnel) {
        setCurrentStep(step1);
      }
    },
  };

  const step3 = {
    id: 'step3',
    title: 'Deployment details',
    showBackButton: () => true,
    getSecondaryText: () => '',
    backActionCallback: () => {
      setCurrentStep(step2);
    },
  };

  const getInitialStep = () => {
    if (initialStep) {
      if (initialStep === 'step1') return step1;
      if (initialStep === 'step2') return step2;
      if (initialStep === 'step3') return step3;
    }
    return hasOnlyOneFunnel ? step2 : step1;
  };

  const [currentStep, setCurrentStep] = useState(getInitialStep());

  const closeHandler = (ev: any, reason?: string) => {
    if (reason === 'backdropClick' || reason === 'closeClick') {
      onCancel();
    }
  };

  const primaryHandler = () => {
    if (currentStep.id === step1.id) {
      setCurrentStep(step2);
    } else {
      onConfirmDeploy();
    }
  };
  const secondaryHandler = () => {
    if (currentStep.id === step2.id) {
      setCurrentStep(step3);
    }
  };

  const actionsJustifyContent =
    currentStep.getSecondaryText() !== '' ? 'space-between' : 'flex-end';

  const { checkAllPermissionsForOrg } = usePermissions();

  let deployPermission: 'ALLOWED' | 'REQUEST_TO_DEPLOY' | 'NOT_ALLOWED' = checkAllPermissionsForOrg(
    ['create:deploy-job'],
    selectedOrgId ?? '',
  )
    ? 'ALLOWED'
    : 'NOT_ALLOWED';
  if (
    deployPermission === 'NOT_ALLOWED' &&
    checkAllPermissionsForOrg(['create:request-changes'], selectedOrgId ?? '')
  ) {
    deployPermission = 'REQUEST_TO_DEPLOY';
  }

  const getConfirmButton = () => {
    if (currentStep.id === step1.id) {
      return (
        <Button dataTestId="confirm-dialog-confirm-button" onClick={primaryHandler} size="small">
          Next
        </Button>
      );
    }
    if (deployPermission === 'REQUEST_TO_DEPLOY') {
      return (
        <Button dataTestId="confirm-dialog-confirm-button" onClick={onRequestChanges} size="small">
          Request to deploy
        </Button>
      );
    }
    if (deployPermission === 'ALLOWED') {
      return (
        <Button dataTestId="confirm-dialog-confirm-button" onClick={onConfirmDeploy} size="small">
          Deploy
        </Button>
      );
    }
    if (deployPermission === 'NOT_ALLOWED') {
      return null;
    }
  };

  return (
    <BaseDialog
      open={true}
      onClose={closeHandler}
      PaperProps={{
        sx: { '.MuiDialogContent-root': { p: 0, overflowX: 'hidden', minWidth: '600px' } },
      }}
      sx={{
        '.confirm-dialog-header': { pb: 2 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
        }}
        className="confirm-dialog-header"
      >
        <Box display="flex" alignItems="center" gap={1} width="100%">
          {currentStep.showBackButton() && (
            <IconButton variant="flat" size="small" onClick={currentStep.backActionCallback}>
              <ArrowLeft />
            </IconButton>
          )}
          <Box data-testid="confirm-dialog-title" width="100%">
            <Typography variant="h3">{currentStep.title}</Typography>
          </Box>
        </Box>
        <Box>
          <IconButton onClick={(ev) => closeHandler(ev, 'closeClick')} variant="flat" size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {currentStep.id === step1.id && (
          <FunnelToDeploy
            funnels={funnels}
            selectedFunnelId={selectedFunnelId}
            setSelectedFunnelId={setSelectedFunnelId}
            initialCrmOrgId={initialCrmOrgId}
          />
        )}
        {currentStep.id === step2.id && (
          <EnvironmentToDeploy
            orgs={orgs}
            setSelectedOrgId={setSelectedOrgId}
            selectedOrgId={selectedOrgId ?? ''}
            showNote={deployPermission === 'REQUEST_TO_DEPLOY'}
            onNoteChange={onNoteChange}
          />
        )}
        {currentStep.id === step3.id && (
          <Box pt={0} px={4}>
            <Box mb={2}>
              <Typography variant="body" color={colors.grey[700]}>
                Summary of changes
              </Typography>
            </Box>
            <DeploymentDiff crmOrgId={selectedOrgId} funnelId={selectedFunnelId} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: actionsJustifyContent }}>
        {currentStep.getSecondaryText() !== '' && (
          <Button
            variant="flat-blue"
            dataTestId="confirm-dialog-cancel-button"
            onClick={(e: any) => {
              secondaryHandler();
              closeHandler(e);
            }}
            size="small"
          >
            {currentStep.getSecondaryText()}
          </Button>
        )}
        {getConfirmButton()}
      </DialogActions>
    </BaseDialog>
  );
};
export default DeployContent;
